import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { IUserState } from '@/types/admin/navigation';

/**
 * Selectors for admin user state
 * Matches the pattern from admin-web
 */

// Base selector for admin user state
export const selectAdminUser = (state: RootState): IUserState => state.adminUser;

// Selector for admin user authentication status
export const selectIsAdminAuthenticated = createSelector(
  [selectAdminUser],
  (adminUser) => adminUser.id > 0 && !!adminUser.email
);

// Selector for admin user loading state
export const selectAdminUserLoading = createSelector(
  [selectAdminUser],
  (adminUser) => adminUser.isLoading || false
);

// Selector for admin user organizations
export const selectAdminUserOrganizations = createSelector(
  [selectAdminUser],
  (adminUser) => adminUser.organizations || []
);

// Selector for admin user properties
export const selectAdminUserProperties = createSelector(
  [selectAdminUser],
  (adminUser) => adminUser.properties || []
);

// Selector for selected organization
export const selectSelectedOrganization = createSelector(
  [selectAdminUser],
  (adminUser) => {
    const organizations = adminUser.organizations || [];
    return organizations.find(org => org.id === adminUser.selectedOrganizationId);
  }
);

// Selector for selected property
export const selectSelectedProperty = createSelector(
  [selectAdminUser],
  (adminUser) => {
    const properties = adminUser.properties || [];
    return properties.find(prop => prop.id === adminUser.selectedPropertyId);
  }
);

// Selector for properties filtered by selected organization
export const selectPropertiesForSelectedOrganization = createSelector(
  [selectAdminUser],
  (adminUser) => {
    const properties = adminUser.properties || [];
    return properties.filter(prop => prop.organizationId === adminUser.selectedOrganizationId);
  }
);

// Selector for admin user roles
export const selectAdminUserRoles = createSelector(
  [selectAdminUser],
  (adminUser) => adminUser.roles || []
);

// Selector for admin user role names
export const selectAdminUserRoleNames = createSelector(
  [selectAdminUserRoles],
  (roles) => roles.map(role => role.name)
);

// Selector for super user status
export const selectIsAdminSuperUser = createSelector(
  [selectAdminUser],
  (adminUser) => adminUser.isSuperUser === true
);
